import { Injectable } from '@angular/core';
import { AngularFirestore, AngularFirestoreCollection, AngularFirestoreDocument } from 'angularfire2/firestore';
import { Observable } from 'rxjs/Observable';
import { Globals } from '../globals';

interface CarrierReport {
    active: number;
    createdAt: any;
    employeeId: string;
    endTime: any;
    productionOrderId: string;
    reportDate: any;
    startTime: any;
    updatedAt: any;
    captureUser?: string;
    name?: string;
}

interface CarrierReportId extends CarrierReport {
    id: string;
}

interface EmployeeReport {
    activitiesCapture: number;
    createdAt: any;
    employeeId: string;
    employeeName: string;
    endTime: any;
    startTime: any;
    updatedAt: any;
    workShiftId: any;
    workShiftName: any;
}

interface EmployeeReportId extends EmployeeReport {
    id: string;
}

@Injectable()
export class CarrierReportService {

    carrierReportCollection: AngularFirestoreCollection<CarrierReport>;
    carrierReports: any;

    carrierReportDocument: AngularFirestoreDocument<CarrierReportId>;
    carrierReport: Observable<any>;

    employeeReportCollection: AngularFirestoreCollection<EmployeeReport>;
    employeesReport: any;

    collection: string;
    collectionProductionOrder: string;
    collectionEmployee: string;

    constructor(private afs: AngularFirestore) {
        this.collection = Globals.CARRIER_REPORT_COLLECTION;
        this.collectionProductionOrder = Globals.PRODUCTION_ORDER_COLLECTION;
        this.collectionEmployee = Globals.EMPLOYEE_COLLECTION;
    }

    postCarrierReport(carrierReportObject: any) {
        this.carrierReportCollection = this.afs.collection(this.collection);

        return this.carrierReportCollection.add(carrierReportObject).then(function(docRef) {
            // console.log('Document added with ID: ', docRef.id);
            return docRef.id;
        }).catch(function(error) {
            // console.error('Error adding document: ', error);
        });
    }

    getCarrierReports({employeeId='', limit=0}={}) {
        if(employeeId){
            this.carrierReportCollection = this.afs.collection(this.collection, ref => ref.where('active', '==', 1).where('employeeId', '==', employeeId));
        }else{
            if(!limit){
                this.carrierReportCollection = this.afs.collection(this.collection, ref => ref.where('active', '==', 1));
            }else{
                this.carrierReportCollection = this.afs.collection(this.collection, ref => ref.where('active', '==', 1).orderBy('createdAt', 'desc').limit(limit));
            }
        }
        this.carrierReports = this.carrierReportCollection.snapshotChanges().map(actions => {
            return actions.map(a => {
                const id$ = a.payload.doc.id;

                const data = a.payload.doc.data() as CarrierReport;

                const productionOrderId = data.productionOrderId;
                const employeeId = data.employeeId;
                const reportDate = data.reportDate;
                const captureUser = data.captureUser;
                const name = data.name;

                return { id$, productionOrderId, employeeId, reportDate, captureUser, name };
            });
        }).map(documents => {
            return documents.map(document => {
                const productionOrder = this.afs.doc(this.collectionProductionOrder + '/' + document.productionOrderId).
                                        snapshotChanges().map(doc => {
                    const data = doc.payload.data();
                    const productionOrderName = data.name;
                    return { productionOrderName };
                });

                const employee = this.afs.doc(this.collectionEmployee + '/' + document.employeeId).snapshotChanges().map(doc => {
                    const data = doc.payload.data();
                    const employeeName = data.name + ' ' + data.lastName;
                    return { employeeName };
                });

                const employeesRef = this.afs.doc(this.collection + '/' + document.id$).collection('employees')
                                    .snapshotChanges().map(docs => {
                    return docs.map(item => {
                        const id$ = item.payload.doc.id;
                        return { id$ };
                    });
                });

                return Observable.combineLatest(productionOrder, employee, employeesRef,
                                                (productionOrderData, employeeData, employeesData) => {
                    return {
                        productionOrder: productionOrderData,
                        employee: employeeData,
                        employees: employeesData
                    };
                }).map(data => {
                    return {
                        productionOrderObj: data.productionOrder,
                        employeeObj: data.employee,
                        employees: data.employees,
                        ...document
                    };
                });
            });
        }).mergeMap(object => {
            return object.length === 0 ? Observable.of([]) : Observable.combineLatest(object);
        });

        return this.carrierReports;
    }

    getCarrierReport(carrierId: string) {
        this.carrierReportDocument = this.afs.doc(this.collection + '/' + carrierId);

        this.carrierReport = this.carrierReportDocument.snapshotChanges().map(action => {
            const id = action.payload.id;

            const data = action.payload.data() as CarrierReport;

            return { id, ...data };
        }).map(document => {
            const productionOrder = this.afs.doc(this.collectionProductionOrder + '/' + document.productionOrderId).
                                    snapshotChanges().map(doc => {
                const data = doc.payload.data();
                const productionOrderName = data.name;
                return { productionOrderName };
            });

            const employee = this.afs.doc(this.collectionEmployee + '/' + document.employeeId).snapshotChanges().map(doc => {
                const data = doc.payload.data();
                const employeeName = data.name + ' ' + data.lastName;
                return { employeeName };
            });

            return Observable.combineLatest(productionOrder, employee, (productionOrderData, employeeData) => {
                return {
                    productionOrder: productionOrderData,
                    employee: employeeData
                };
            }).map(data => {
                return {
                    productionOrderObj: data.productionOrder,
                    employeeObj: data.employee,
                    ...document
                };
            });
        }).mergeMap(object => Observable.combineLatest(object));

        return this.carrierReport;
    }

    putCarrierReport(carrierReportObject: any, id$: string) {
        this.carrierReportCollection = this.afs.collection(this.collection);

        return this.carrierReportCollection.doc(id$).update(carrierReportObject).then(function() {
            // console.log('Document updated');
        }).catch(function(error) {
            // console.error('Error updating document: ', error);
        });
    }

    /*
    // Method to delete the register from DB
    delCarrierReport(id$: string) {
        this.carrierReportDocument = this.afs.doc(this.collection + '/' + id$);

        return this.carrierReportDocument .delete().then(function() {
            // console.log('Document deleted');
        }).catch(function(error) {
            // console.error('Error deleting document: ', error);
        });
    }
    */

    delCarrierReport(id$: string) {
        if(!confirm('¿Desea eliminar?')) return;
        this.carrierReportCollection = this.afs.collection(this.collection);

        return this.carrierReportCollection.doc(id$).update({ active: 0, updatedAt: new Date() }).then(function() {
            // console.log('Document updated');
        }).catch(function(error) {
            // console.error('Error updating document: ', error);
        });
    }

    postEmployeeReport(employeeReportObject: any, carrierReportId: string) {
        this.carrierReportCollection = this.afs.collection(this.collection);

        return this.carrierReportCollection.doc(carrierReportId).collection('employees').
                                              add(employeeReportObject).then(function(docRef) {
            // console.log('Document added with ID: ', docRef.id);
            return docRef.id;
        }).catch(function(error) {
            // console.error('Error adding document: ', error);
        });
    }

    getEmployeesReport(carrierReportId: string) {
        this.employeeReportCollection = this.afs.doc(this.collection + '/' + carrierReportId).
            collection('employees', ref => ref.orderBy('createdAt', 'desc'));

        this.employeesReport = this.employeeReportCollection.snapshotChanges().map(actions => {
            return actions.map(a => {
                const id$ = a.payload.doc.id;

                const data = a.payload.doc.data() as EmployeeReport;

                const employeeName = data.employeeName;

                let workShiftName = '';
                if (data.activitiesCapture === 1) {
                    workShiftName = data.startTime + ' - ' + data.endTime;
                } else {
                    workShiftName = data.workShiftName;
                }

                const employeeId = data.employeeId;
                const startTime = data.startTime;
                const endTime = data.endTime;

                return { id$, employeeName, workShiftName, employeeId, startTime, endTime };
            });
        }).map(object => object);

        return this.employeesReport;
    }

    delEmployeeReport(id$: string, carrierReportId: string) {
        return this.afs.collection(this.collection).doc(carrierReportId).collection('employees').doc(id$).delete().then(function() {
            // console.log('Document deleted');
        }).catch(function(error) {
            // console.error('Error deleting document: ', error);
        });
    }

    getCarrierReportsByReportDate(iReportDate: any) {
        this.carrierReportCollection = this.afs.collection(this.collection,
            ref => ref.where('active', '==', 1).where('reportDate', '==', iReportDate));

        this.carrierReports = this.carrierReportCollection.snapshotChanges().map(actions => {
            return actions.map(a => {
                const id$ = a.payload.doc.id;

                const data = a.payload.doc.data() as CarrierReport;

                const reportDate = data.reportDate;
                const startTime = data.startTime;
                const endTime = data.endTime;
                const employeeId = data.employeeId;

                return { id$, reportDate, startTime, endTime, employeeId };
            });
        }).map(documents => {
            return documents.map(document => {
                this.employeeReportCollection = this.afs.doc(this.collection + '/' + document.id$).
                                                collection('employees', ref => ref.orderBy('createdAt', 'desc'));

                const employeesRef = this.employeeReportCollection.snapshotChanges().map(actions => {
                    return actions.map(a => {
                        const id$ = a.payload.doc.id;

                        const data = a.payload.doc.data() as EmployeeReport;

                        const employeeId = data.employeeId;
                        const employeeName = data.employeeName;
                        const startTime = data.startTime;
                        const endTime = data.endTime;

                        return { id$, employeeId, employeeName, startTime, endTime };
                    });
                });

                return Observable.combineLatest(employeesRef, (employeesData) => {
                    return {
                        employees: employeesData
                    };
                }).map(data => {
                    return {
                        employees: data.employees,
                        ...document
                    };
                });
            });
        }).mergeMap(object => {
            return object.length === 0 ? Observable.of([]) : Observable.combineLatest(object);
        });

        return this.carrierReports;
    }

    getCarrierReportsByRangeDate(iReportDate: any, eReportDate: any) {
        this.carrierReportCollection = this.afs.collection(this.collection, ref => ref.where('active', '==', 1)
                                       .where('reportDate', '>=', iReportDate).where('reportDate', '<=', eReportDate));

        this.carrierReports = this.carrierReportCollection.snapshotChanges().map(actions => {
            return actions.map(a => {
                const id$ = a.payload.doc.id;

                const data = a.payload.doc.data() as CarrierReport;

                const name = data.name;
                const reportDate = data.reportDate;
                const employeeId = data.employeeId;

                return { id$, name, reportDate, employeeId };
            });
        }).map(documents => {
            return documents.map(document => {
                this.employeeReportCollection = this.afs.doc(this.collection + '/' + document.id$).
                                                collection('employees', ref => ref.orderBy('createdAt', 'desc'));

                const employeesRef = this.employeeReportCollection.snapshotChanges().map(actions => {
                    return actions.map(a => {
                        const id$ = a.payload.doc.id;

                        return { id$ };
                    });
                });

                return Observable.combineLatest(employeesRef, (employeesData) => {
                    return {
                        employees: employeesData
                    };
                }).map(data => {
                    return {
                        employees: data.employees,
                        ...document
                    };
                });
            });
        }).mergeMap(object => {
            return object.length === 0 ? Observable.of([]) : Observable.combineLatest(object);
        });

        return this.carrierReports;
    }

    getCarrierReportsSinceReportDate(iReportDate: any) {
        this.carrierReportCollection = this.afs.collection(this.collection, ref => ref.where('active', '==', 1)
                                       .where('reportDate', '>=', iReportDate));

        this.carrierReports = this.carrierReportCollection.snapshotChanges().map(actions => {
            return actions.map(a => {
                const id$ = a.payload.doc.id;

                const data = a.payload.doc.data() as CarrierReport;

                const productionOrderId = data.productionOrderId;
                const employeeId = data.employeeId;
                const reportDate = data.reportDate;
                const captureUser = data.captureUser;
                const name = data.name;

                return { id$, productionOrderId, employeeId, reportDate, captureUser, name };
            });
        }).map(documents => {
            return documents.map(document => {
                const productionOrder = this.afs.doc(this.collectionProductionOrder + '/' + document.productionOrderId).
                                        snapshotChanges().map(doc => {
                    const data = doc.payload.data();
                    const productionOrderName = data.name;
                    return { productionOrderName };
                });

                const employee = this.afs.doc(this.collectionEmployee + '/' + document.employeeId).snapshotChanges().map(doc => {
                    const data = doc.payload.data();
                    const employeeName = data.name + ' ' + data.lastName;
                    return { employeeName };
                });

                const employeesRef = this.afs.doc(this.collection + '/' + document.id$).collection('employees')
                                    .snapshotChanges().map(docs => {
                    return docs.map(item => {
                        const id$ = item.payload.doc.id;
                        return { id$ };
                    });
                });

                return Observable.combineLatest(productionOrder, employee, employeesRef,
                                                (productionOrderData, employeeData, employeesData) => {
                    return {
                        productionOrder: productionOrderData,
                        employee: employeeData,
                        employees: employeesData
                    };
                }).map(data => {
                    return {
                        productionOrderObj: data.productionOrder,
                        employeeObj: data.employee,
                        employees: data.employees,
                        ...document
                    };
                });
            });
        }).mergeMap(object => {
            return object.length === 0 ? Observable.of([]) : Observable.combineLatest(object);
        });

        return this.carrierReports;
    }

}

